import React from 'react';
import '../templates.css';

/**
 * TC Classic Template — matches ERP default green-themed TC layout
 */
const TCClassic = ({ data, schoolInfo }) => {
  const d = data || {};
  const school = schoolInfo || {};
  const formatDate = (dt) => dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

  const certNumber = d.Certificate_Number || d.Certificate_Id || 'TC/XXXX/XXXXX';
  const certDate = d.Certificate_Date ? formatDate(d.Certificate_Date) : '';

  /* --- Section header --- */
  const SH = ({ label }) => (
    <div style={{ background: 'rgba(27,94,32,0.07)', borderRadius: 2, padding: '1px 10px', margin: '3px 0 3px 0' }}>
      <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#1b5e20', letterSpacing: 0.5 }}>{label}</span>
    </div>
  );

  /* --- Full-width row --- */
  const R = ({ label, value, bold }) => (
    <div style={{ display: 'flex', borderBottom: '1px dashed #e8e8e8', padding: '2px 6px' }}>
      <span style={{ width: '38%', fontSize: '0.64rem', fontWeight: 600, color: '#555', flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1, fontSize: '0.64rem', color: bold ? '#1b5e20' : '#222', fontWeight: bold ? 700 : 400 }}>{value}</span>
    </div>
  );

  /* --- Two-column row --- */
  const R2 = ({ l1, v1, l2, v2 }) => (
    <div style={{ display: 'flex', borderBottom: '1px dashed #e8e8e8', padding: '2px 6px' }}>
      <span style={{ width: '19%', fontSize: '0.64rem', fontWeight: 600, color: '#555', flexShrink: 0 }}>{l1}</span>
      <span style={{ width: '31%', fontSize: '0.64rem', color: '#222', flexShrink: 0 }}>{v1}</span>
      {l2 && (
        <>
          <span style={{ width: '19%', fontSize: '0.64rem', fontWeight: 600, color: '#555', flexShrink: 0, borderLeft: '1px dashed #e0e0e0', paddingLeft: 6 }}>{l2}</span>
          <span style={{ flex: 1, fontSize: '0.64rem', color: '#222' }}>{v2}</span>
        </>
      )}
    </div>
  );

  /* --- Build section arrays, skip empty values --- */
  const pA = [];
  if (d.Student_Name) pA.push(['Student Name', d.Student_Name]);
  if (d.Father_Name) pA.push(["Father's Name", d.Father_Name]);
  if (d.Mother_Name) pA.push(["Mother's Name", d.Mother_Name]);
  if (d.Guardian_Name) pA.push(['Guardian Name', d.Guardian_Name]);
  if (d.Date_Of_Birth) pA.push(['Date of Birth', formatDate(d.Date_Of_Birth)]);
  if (d.Date_Of_Birth_Words) pA.push(['DOB (Words)', d.Date_Of_Birth_Words]);
  if (d.Gender) pA.push(['Gender', d.Gender]);
  if (d.Nationality) pA.push(['Nationality', d.Nationality]);
  if (d.Religion) pA.push(['Religion', d.Religion]);
  if (d.Caste) pA.push(['Caste', d.Caste]);
  if (d.Category) pA.push(['Category', d.Category]);
  if (d.Aadhaar_Number) pA.push(['Aadhaar Number', d.Aadhaar_Number]);
  if (d.PEN) pA.push(['PEN', d.PEN]);

  const pB = [];
  if (d.Admission_Number) pB.push(['Admission No.', d.Admission_Number]);
  if (d.Certificate_Number) pB.push(['Certificate No.', d.Certificate_Number]);
  if (d.Book_Number) pB.push(['Book No.', d.Book_Number]);
  if (d.Date_Of_First_Admission) pB.push(['Date of First Adm.', formatDate(d.Date_Of_First_Admission)]);
  if (d.Class_First_Admitted) pB.push(['Class First Admitted', d.Class_First_Admitted]);
  if (d.Class_Last_Studied) pB.push(['Class Last Studied', d.Class_Last_Studied]);
  if (d.Section_Last_Studied) pB.push(['Section Last Studied', d.Section_Last_Studied]);
  if (d.Academic_Session) pB.push(['Academic Session', d.Academic_Session]);
  if (d.School_Board) pB.push(['School Board', d.School_Board]);
  if (d.Medium_Of_Instruction) pB.push(['Medium of Instruction', d.Medium_Of_Instruction]);
  if (d.Qualified_For_Promotion) pB.push(['Qualified for Promotion', d.Qualified_For_Promotion]);
  if (d.Exam_Result) pB.push(['Exam Result', d.Exam_Result]);

  const bWide = [];
  if (d.Subjects_Studied) bWide.push(['Subjects Studied', d.Subjects_Studied]);

  const pC = [];
  if (d.Total_Working_Days) pC.push(['Total Working Days', d.Total_Working_Days]);
  if (d.Total_Days_Attended) pC.push(['Total Days Attended', d.Total_Days_Attended]);
  if (d.Conduct) pC.push(['Conduct', d.Conduct]);

  const pD = [];
  if (d.NCC_Scout_Guide) pD.push(['NCC / Scout & Guide', d.NCC_Scout_Guide]);
  if (d.Games_Played) pD.push(['Games Played', d.Games_Played]);
  if (d.Height_At_Leaving) pD.push(['Height at Leaving', d.Height_At_Leaving]);
  if (d.Weight_At_Leaving) pD.push(['Weight at Leaving', d.Weight_At_Leaving]);
  if (d.Is_Disabled) pD.push(['Is Disabled', d.Is_Disabled]);
  if (d.Disability_Details) pD.push(['Disability Details', d.Disability_Details]);
  const dWide = [];
  if (d.Extra_Curricular) dWide.push(['Extra-curricular Activities', d.Extra_Curricular]);

  const pE = [];
  if (d.Date_Of_Leaving) pE.push(['Date of Leaving', formatDate(d.Date_Of_Leaving)]);
  if (d.All_Dues_Cleared) pE.push(['All Dues Cleared', d.All_Dues_Cleared]);
  if (d.TC_Issue_Date) pE.push(['TC Issue Date', formatDate(d.TC_Issue_Date)]);
  const eWide = [];
  if (d.Reason_For_Leaving) eWide.push(['Reason for Leaving', d.Reason_For_Leaving]);
  if (d.Remarks) eWide.push(['Remarks', d.Remarks]);

  /* Render array in paired rows */
  const renderPairs = (arr) => {
    const rows = [];
    for (let i = 0; i < arr.length; i += 2) {
      if (i + 1 < arr.length) {
        rows.push(<R2 key={i} l1={arr[i][0]} v1={arr[i][1]} l2={arr[i + 1][0]} v2={arr[i + 1][1]} />);
      } else {
        rows.push(<R key={i} label={arr[i][0]} value={arr[i][1]} />);
      }
    }
    return rows;
  };

  return (
    <div className="tpl-page" style={{ padding: 0, fontFamily: "'Arial', sans-serif", position: 'relative', overflow: 'hidden' }}>
      {/* Outer green border */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, border: '3px solid #1b5e20' }} />
      {/* Inner border */}
      <div style={{ position: 'absolute', top: 4, left: 4, right: 4, bottom: 4, border: '1px solid #4caf50', pointerEvents: 'none', zIndex: 0 }} />

      {/* Green Header Bar */}
      <div style={{ margin: '10px 10px 0 10px', background: '#1b5e20', borderRadius: 4, padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 16, position: 'relative', zIndex: 1 }}>
        {school.schoolLogo && <img src={school.schoolLogo} alt="Logo" style={{ height: 50, width: 50, objectFit: 'contain', borderRadius: 4 }} />}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '1.3rem', letterSpacing: 1.5, color: '#fff', fontFamily: "'Georgia', serif", textTransform: 'uppercase' }}>
            {school.schoolName || 'School Name'}
          </div>
          {school.schoolAddress && <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.85)' }} dangerouslySetInnerHTML={{ __html: school.schoolAddress.replace(/<br\s*\/?>/gi, '<br/>') }} />}
          {school.schoolEmail && <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.7)' }}>Email: {school.schoolEmail}</div>}
        </div>
      </div>

      {/* Green accent line */}
      <div style={{ margin: '4px 10px 0 10px', height: 2, background: '#4caf50', borderRadius: 2 }} />

      {/* Title + UDISE */}
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.95rem', marginTop: 6, color: '#1b5e20', textDecoration: 'underline', letterSpacing: 2, fontFamily: "'Georgia', serif" }}>
        TRANSFER CERTIFICATE
      </div>
      {school.udise && (
        <div style={{ textAlign: 'center', fontSize: '0.58rem', color: '#555' }}>
          UDISE: <b style={{ color: '#1b5e20' }}>{school.udise}</b>
        </div>
      )}

      {/* TC No & Date row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 28px 6px' }}>
        <span style={{ fontSize: '0.64rem', color: '#555' }}>TC No: <b style={{ color: '#1b5e20' }}>{certNumber}</b></span>
        <span style={{ fontSize: '0.64rem', color: '#555' }}>Date: <b>{certDate}</b></span>
      </div>

      {/* Content area */}
      <div style={{ padding: '0 20px' }}>
        {/* A - Personal */}
        <SH label="A. STUDENT PERSONAL DETAILS" />
        {renderPairs(pA)}
        {d.Address && <R label="Address" value={d.Address} />}

        {/* B - Academic */}
        <SH label="B. ADMISSION & ACADEMIC DETAILS" />
        {renderPairs(pB)}
        {bWide.map(([l, v], i) => <R key={`bw${i}`} label={l} value={v} />)}

        {/* C - Attendance */}
        {pC.length > 0 && (
          <>
            <SH label="C. ATTENDANCE & CONDUCT" />
            {renderPairs(pC)}
          </>
        )}

        {/* D - Extra */}
        {(pD.length > 0 || dWide.length > 0) && (
          <>
            <SH label="D. EXTRA-CURRICULAR & OTHER DETAILS" />
            {renderPairs(pD)}
            {dWide.map(([l, v], i) => <R key={`dw${i}`} label={l} value={v} />)}
          </>
        )}

        {/* E - Leaving */}
        {(pE.length > 0 || eWide.length > 0) && (
          <>
            <SH label="E. LEAVING DETAILS" />
            {renderPairs(pE)}
            {eWide.map(([l, v], i) => <R key={`ew${i}`} label={l} value={v} />)}
          </>
        )}

        {/* Certification note */}
        <p style={{ fontSize: '0.58rem', color: '#444', marginTop: 8, marginBottom: 4, textAlign: 'justify', lineHeight: 1.4 }}>
          Certified that the above information is in accordance with the school records. This certificate is issued on the request of the student / parent / guardian.
        </p>
      </div>

      {/* Signatures — two columns */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 32px', marginTop: 40 }}>
        {['Office Clerk', 'Principal / Head of School'].map(role => (
          <div key={role} style={{ textAlign: 'center', minWidth: 110 }}>
            <div style={{ borderTop: '1px solid #666', width: 110, margin: '0 auto 3px' }} />
            <span style={{ fontSize: '0.58rem', color: '#555' }}>{role}</span>
          </div>
        ))}
      </div>

      {/* School Seal */}
      <div style={{ textAlign: 'center', marginTop: 8 }}>
        <div style={{ width: 50, height: 50, borderRadius: '50%', border: '2px dashed #4caf50', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.45rem', color: '#999', lineHeight: 1.2, textAlign: 'center' }}>School<br />Seal</span>
        </div>
      </div>

      {/* Bottom accent bar */}
      <div style={{ position: 'absolute', bottom: 8, left: 8, right: 8, height: 4, background: '#1b5e20', borderRadius: 2 }} />
    </div>
  );
};

/**
 * TC Modern Template
 */
const TCModern = ({ data, schoolInfo }) => {
  const d = data || {};
  const school = schoolInfo || {};
  const formatDate = (dt) => dt ? new Date(dt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '';

  const Field = ({ label, value }) => (
    <div className="tpl-mod-field">
      <span className="tpl-mod-label">{label}</span>
      <span className="tpl-mod-value">{value || '—'}</span>
    </div>
  );

  return (
    <div className="tpl-page tpl-tc-modern">
      <div className="tpl-mod-header">
        <div className="tpl-mod-header-bar tpl-bar-blue" />
        <div className="tpl-mod-header-content">
          {school.schoolLogo && <img src={school.schoolLogo} alt="" className="tpl-logo-sm" />}
          <div>
            <h1 className="tpl-mod-school">{school.schoolName || 'School Name'}</h1>
            {school.schoolAddress && <p className="tpl-mod-addr" dangerouslySetInnerHTML={{ __html: school.schoolAddress.replace(/<br\s*\/?>/gi, '<br/>') }} />}
            {school.schoolEmail && <p className="tpl-mod-addr">{school.schoolEmail}</p>}
          </div>
        </div>
        <div className="tpl-mod-title tpl-title-blue">Transfer Certificate</div>
        <div className="tpl-mod-certno">No: {d.Certificate_Number || d.Certificate_Id || '—'}</div>
      </div>

      <div className="tpl-mod-grid">
        <Field label="Student Name" value={d.Student_Name} />
        <Field label="Father's Name" value={d.Father_Name} />
        <Field label="Mother's Name" value={d.Mother_Name} />
        <Field label="Date of Birth" value={formatDate(d.Date_Of_Birth)} />
        <Field label="Gender" value={d.Gender} />
        <Field label="Admission No" value={d.Admission_Number} />
        <Field label="Class Last Studied" value={d.Class_Last_Studied} />
        <Field label="Section" value={d.Section_Last_Studied} />
        <Field label="Academic Session" value={d.Academic_Session} />
        <Field label="Conduct" value={d.Conduct} />
        <Field label="Date of Leaving" value={formatDate(d.Date_Of_Leaving)} />
        <Field label="Reason" value={d.Reason_For_Leaving} />
        <Field label="All Dues Cleared" value={d.All_Dues_Cleared} />
        <Field label="TC Issue Date" value={formatDate(d.TC_Issue_Date)} />
      </div>

      {d.Remarks && <div className="tpl-mod-remarks"><strong>Remarks:</strong> {d.Remarks}</div>}

      <div className="tpl-mod-footer">
        <span>Date: {formatDate(d.Certificate_Date) || formatDate(new Date())}</span>
        <div className="tpl-mod-sigs">
          <div className="tpl-sig"><div className="tpl-sig-line" /><span>Principal</span></div>
        </div>
      </div>
    </div>
  );
};

export { TCClassic, TCModern };
